import type { SaleRow } from '../lib/api';
import { formatCurrency, formatDateTime } from '../lib/format';
import { StatusBadge } from './StatusBadge';

interface SalesTableProps {
  rows: SaleRow[];
  loading: boolean;
  page: number;
  hasMore: boolean;
  onPageChange: (page: number) => void;
  affiliateColumn?: (row: SaleRow) => string;
  // Exclusivo do admin (ver AdminDashboardPage.tsx) — só é passado ali,
  // nunca em InfluencerDashboardPage.tsx. Sem esse prop, a coluna
  // simplesmente não existe na tabela — é assim que o parceiro nunca vê
  // a taxa/líquido, sem precisar de nenhum "if é admin" aqui dentro.
  netAfterFeesColumn?: (row: SaleRow) => string;
}

export function SalesTable({ rows, loading, page, hasMore, onPageChange, affiliateColumn, netAfterFeesColumn }: SalesTableProps) {
  return (
    <div className="card overflow-hidden">
      {/* Desktop/tablet: tabela normal. */}
      <div className="hidden overflow-x-auto md:block">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-[rgb(var(--border))] text-left text-xs uppercase tracking-wide text-[rgb(var(--text-muted))]">
              <th className="px-4 py-3 font-medium">Data e hora</th>
              <th className="px-4 py-3 font-medium">Produto</th>
              {affiliateColumn ? <th className="px-4 py-3 font-medium">Afiliado</th> : null}
              <th className="px-4 py-3 font-medium">Transação</th>
              <th className="px-4 py-3 font-medium text-right">Valor</th>
              <th className="px-4 py-3 font-medium text-right">Comissão</th>
              {netAfterFeesColumn ? <th className="px-4 py-3 font-medium text-right">Líquido após taxas</th> : null}
              <th className="px-4 py-3 font-medium">Status</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.id} className="border-b border-[rgb(var(--border))] last:border-0">
                <td className="whitespace-nowrap px-4 py-3">{formatDateTime(row.purchasedAt)}</td>
                <td className="px-4 py-3">{row.product ?? 'N/D'}</td>
                {affiliateColumn ? <td className="px-4 py-3">{affiliateColumn(row)}</td> : null}
                <td className="whitespace-nowrap px-4 py-3 font-mono text-xs text-[rgb(var(--text-muted))]">{row.transaction}</td>
                <td className="whitespace-nowrap px-4 py-3 text-right">{formatCurrency(row.saleValue, row.currency)}</td>
                <td className="whitespace-nowrap px-4 py-3 text-right">{formatCurrency(row.commissionValue, row.currency)}</td>
                {netAfterFeesColumn ? (
                  <td className="whitespace-nowrap px-4 py-3 text-right">{netAfterFeesColumn(row)}</td>
                ) : null}
                <td className="whitespace-nowrap px-4 py-3">
                  <StatusBadge status={row.status} label={row.statusLabel} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile: cartões empilhados. */}
      <div className="divide-y divide-[rgb(var(--border))] md:hidden">
        {rows.map((row) => (
          <div key={row.id} className="space-y-1.5 p-4">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium">{row.product ?? 'N/D'}</span>
              <StatusBadge status={row.status} label={row.statusLabel} />
            </div>
            <div className="flex items-center justify-between text-sm">
              <span className="text-[rgb(var(--text-muted))]">{formatDateTime(row.purchasedAt)}</span>
              <span className="font-mono text-xs text-[rgb(var(--text-muted))]">{row.transaction}</span>
            </div>
            {affiliateColumn ? <div className="text-xs text-[rgb(var(--text-muted))]">Afiliado: {affiliateColumn(row)}</div> : null}
            <div className="flex items-center justify-between text-sm">
              <span>Valor: {formatCurrency(row.saleValue, row.currency)}</span>
              <span>Comissão: {formatCurrency(row.commissionValue, row.currency)}</span>
            </div>
            {netAfterFeesColumn ? (
              <div className="text-xs text-[rgb(var(--text-muted))]">Líquido após taxas: {netAfterFeesColumn(row)}</div>
            ) : null}
          </div>
        ))}
      </div>

      {!loading && rows.length === 0 ? (
        <p className="p-8 text-center text-sm text-[rgb(var(--text-muted))]">Nenhuma venda encontrada com esses filtros.</p>
      ) : null}

      <div className="flex items-center justify-between border-t border-[rgb(var(--border))] px-4 py-3">
        <button type="button" className="btn-secondary" disabled={page <= 1 || loading} onClick={() => onPageChange(page - 1)}>
          Anterior
        </button>
        <span className="text-sm text-[rgb(var(--text-muted))]">Página {page}</span>
        <button type="button" className="btn-secondary" disabled={!hasMore || loading} onClick={() => onPageChange(page + 1)}>
          Próxima
        </button>
      </div>
    </div>
  );
}
